import { ReportValueType } from '../../shared-models/reports/report-value-type.enum';
import { ReportCellValue } from '../../shared-models/reports/report-definition.interface';

/**
 * Format a report cell value for display in PDF or previews.
 */
export function formatReportCellValue(
  value: ReportCellValue,
  valueType: ReportValueType = ReportValueType.string,
): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  switch (valueType) {
    case ReportValueType.currency:
      return formatReportCurrency(value);
    case ReportValueType.quantity:
      return formatReportQuantity(value);
    case ReportValueType.number:
      return formatReportNumber(value);
    case ReportValueType.date:
      return formatReportDate(value);
    case ReportValueType.dateTime:
      return formatReportDateTime(value);
    case ReportValueType.string:
    default:
      return String(value);
  }
}

/**
 * Format a currency amount as Rand with two decimal places.
 */
export function formatReportCurrency(value: ReportCellValue): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return '—';
  }
  const sign = numeric < 0 ? '-' : '';
  return `${sign}R ${Math.abs(numeric).toFixed(2)}`;
}

/**
 * Format a quantity with up to two decimal places.
 */
export function formatReportQuantity(value: ReportCellValue): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return '—';
  }
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2);
}

/**
 * Format a generic number with up to two decimal places.
 */
export function formatReportNumber(value: ReportCellValue): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return '—';
  }
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2);
}

/**
 * Format a date-only value for reports.
 */
export function formatReportDate(value: ReportCellValue): string {
  const date = toDate(value);
  if (!date) {
    return '—';
  }
  return date.toLocaleDateString();
}

/**
 * Format a date-time value for reports.
 */
export function formatReportDateTime(value: ReportCellValue): string {
  const date = toDate(value);
  if (!date) {
    return '—';
  }
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${date.toLocaleDateString()} ${hours}:${minutes}`;
}

/**
 * Sum numeric values for a totals column, ignoring non-numeric cells.
 */
export function sumReportColumnValues(values: ReportCellValue[]): number {
  let total = 0;
  for (const value of values) {
    const numeric = toFiniteNumber(value);
    if (numeric !== null) {
      total += numeric;
    }
  }
  return total;
}

/**
 * Convert an unknown cell value into a finite number when possible.
 */
export function toFiniteNumber(value: ReportCellValue): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toDate(value: ReportCellValue): Date | null {
  if (typeof value === 'number' || typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}
